<template>
  <div class="hose-configurator" :class="device">
    <button class="view-cart-button" @click="setCartState" v-if="!isMagento">
      {{ $t('cart.button_text') }}
    </button>

    <transition name="fade" mode="out-in">
      <div class="cart-dropdown"
           v-if="isCartOpen"
           :class="{'active': isCartOpen}">
        <order-cart @close="setCartState"></order-cart>
      </div>
    </transition>

    <div class="container-x" v-if="selectedHose">
      <div id="hose-container" :class="device">
        <popup-controller
            v-if="popupType !== null"
            :popup="popupType"
            @close="closePopup"
        ></popup-controller>

        <hose-display @openModal="showPopup = true"></hose-display>
        <hose-recommendations></hose-recommendations>

        <template v-if="device === 'mobile'">
          <sidebar :title="$t('hose.configurator.menu')">
            <hose-capacity-overview></hose-capacity-overview>
            <SearchBuildModal />
            <hose-settings></hose-settings>
          </sidebar>
        </template>
        <template v-else>
          <hose-capacity-overview></hose-capacity-overview>
          <SearchBuildModal />
          <hose-settings></hose-settings>
        </template>
      </div>
    </div>
    <div class="container-x" v-else style="min-height: 80vh;">
      <app-loader></app-loader>
    </div>
    <popup-shopping-cart :generator="$t('cart.hose')" @close="showPopup = false" v-if="showPopup"></popup-shopping-cart>
  </div>
</template>

<script>

import HoseCapacityOverview from "@/components/hose/HoseCapacityOverview";
import HoseSettings from "@/components/hose/HoseSettings";
import HoseDisplay from "@/components/hose/HoseDisplay";
import HoseRecommendations from "@/components/hose/HoseRecommendations";
import PopupController from "@/components/hose/components/PopupController";

import {mapGetters, mapState} from "vuex";
import AppLoader from "@/components/AppLoader";
import Sidebar from "../sidebar/Sidebar"
import PopupShoppingCart from "@/components/popup/PopupShoppingCart";
import SearchBuild from "@/components/build/SearchBuild.vue";
import OrderCart from "@/components/step2/order/OrderCart.vue";
import { getBuildUidFromUrl } from '../../helpers/route';
import SearchBuildModal from "@/components/build/SearchBuildModal.vue";

export default {
  name: "HoseConfigurator",

  data() {
    return {
      mobileConfig: false,
      showPopup: false,
    }
  },

  components: {
    SearchBuildModal,
    OrderCart,
    SearchBuild,
    PopupController,
    HoseRecommendations,
    HoseDisplay,
    HoseSettings,
    HoseCapacityOverview,
    AppLoader,
    Sidebar,
    PopupShoppingCart,
  },

  computed: {
    ...mapGetters({
      preferences: 'build/hoses/getHosePreferences',
      device: 'getDevice',
      isMagento: 'preference/isMagento',
    }),
    ...mapState('build/hoses', {
      selectedHose: (state) => state.build.selectedHose,
    }),
    ...mapState('preference', {
      isLoading: (state) => state.isLoading,
    }),
    ...mapState({
      isCartOpen: (state) => state.cart.isCartOpen,
    }),
    popupType() {
      return this.preferences.popupType;
    },
  },

  watch: {
    $route (){
      this.hoseInit();
    },
    isLoading: {
      handler(newValue) {
        if (!newValue) {
          this.hoseInit();
        }
      },
      immediate: true,
    },
  },

  methods: {
    setCartState() {
      if (this.isCartOpen) {
        this.$store.commit('cart/setCartState', false);
        return;
      }

      this.$store.commit('cart/setCartState', true);
    },
    hoseInit() {
      const buildUid = getBuildUidFromUrl();

      if (!buildUid) {
        return;
      }

      this.$store.dispatch('build/hoses/fetchBuild', buildUid)
        .then(build => {
          this.$store.dispatch('build/hoses/loadBuild', build);
        })
        .catch(() => {
          this.$store.dispatch('build/hoses/setFirstHose');
          this.$store.dispatch('build/hoses/setFirstAvailableCoupling');
        });
    },

    closePopup() {
      this.$store.commit('build/hoses/closePopup');
    },
  }
}
</script>

<style scoped lang="scss">
.hose-configurator {
  padding-bottom: 20px;
  background-color: #F5F5F5;
  width: 100%;
  position: relative;

  .view-cart-button {
    position: sticky;
    right: 0;
    top: -50px;
    left: 100%;
    z-index: 8;
    background: #FFFFFF;
    color: #71BC03;
    font-weight: 600;
    border: 2px solid #71BC03;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;

    &:hover {
      background: rgba(#71BC03, 10%);
    }

    &:focus {
      outline: none;
    }
  }

  .cart-dropdown {
    z-index: 8;
    background: white;
    min-width: 420px;
    width: auto;
    position: absolute;
    top: 40px;
    right: 0;
    border-radius: 3px;
    padding: 20px;
    overflow: hidden;
    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, .26) !important;

    @media only screen and (max-width: 512px) {
      right: 0;
      width: 100%;
      min-width: 100%;
    }
  }

  .sidebar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #3DB0C7;
    color: #fff;
    position: fixed;
    left: 0;
    top: 100px;
    padding: 0 15px;
    font-family: neue-haas-grotesk-text, sans-serif;
    font-size: 13px;
    transform-origin: top right;
    transform: translateX(-100%) rotate(270deg);
    height: 30px;
    line-height: 30px;
    width: 200px;
    transition: all .3s ease;
    z-index: 3;

    img {
      width: 15px;
      height: 15px;
    }

    &.open {
      left: 100%;
      transform: translateX(-100%) rotate(270deg) translateY(-100%);

      img {
        transform: rotate(180deg);
      }
    }
  }

  &.mobile {
    padding: 100px 0 70px 0;
    overflow-y: scroll;
    box-sizing: border-box;
    max-width: 100vw;

    #hose-container {
      display: grid;
      grid-template-columns: auto;
      grid-template-rows: auto auto auto;
      grid-gap: 0;
      max-width: calc(100vw - 80px);
      margin-left: auto;

      .fade-move-left-enter-active, .fade-move-left-leave-active {
        transition: all .25s ease;
      }

      .fade-move-left-enter, .fade-move-left-leave-to /* .fade-leave-active below version 2.1.8 */
      {
        transform: translateX(-100%);
      }

      .mobile-configuration {
        position: fixed;
        top: 50px;
        right: 30px;
        left: 0;
        bottom: 0;
        z-index: 2;
        padding-top: 36px;
        overflow: scroll;
        box-shadow: 0 5px 15px rgba(0, 0, 0, .1);
        background-color: #f9f9f9;

        .image-container {
          position: relative;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;

          img {
            width: 275px;
            min-width: 80%;
            height: auto;
            max-height: 80vh;
            object-fit: contain;
            object-position: center;
          }
        }
      }
    }
  }

  #hose-container {
    display: grid;
    grid-template-columns: 270px auto;
    grid-template-rows: auto auto auto;
    grid-gap: 15px;
    overflow: auto !important;
  }
}

.magento {
  .hose-configurator {
    padding-top: 0;
    background-color: #FFF;
  }
}
</style>
